/**
 * Retrieves and JSON-deserialises a value from `localStorage`.
 *
 * @typeParam T - The expected type of the stored value.
 * @param key - The localStorage key to read.
 * @returns The deserialised value, or `null` if the key is absent or the
 *          stored string is not valid JSON.
 */
export function getLocalStorage<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    if (raw === null) {
      return null
    }
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

/**
 * JSON-serialises `value` and writes it to `localStorage`.
 *
 * Errors (e.g. storage quota exceeded) are silently swallowed so callers
 * do not need to guard every write.
 *
 * @typeParam T - The type of the value to store.
 * @param key   - The localStorage key to write.
 * @param value - The value to serialise and store.
 */
export function setLocalStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Silently ignore quota / security errors
  }
}
